import { Alert, Select, SimpleGrid, Stack, Text } from "@mantine/core";
import { IconAlertCircle } from "@tabler/icons-react";
import type { ComboboxData } from "@mantine/core";

interface PreferredDeviceSelectionProps {
  cassettePrinterId: string | null;
  slidePrinterId: string | null;
  cassettePrinterOptions: ComboboxData;
  slidePrinterOptions: ComboboxData;
  isLoading: boolean;
  hasLoadingError: boolean;
  hasPartialLoadingError: boolean;
  hasStaleCassettePrinter: boolean;
  hasStaleSlidePrinter: boolean;
  onCassettePrinterChange: (deviceId: string | null) => void;
  onSlidePrinterChange: (deviceId: string | null) => void;
}

export default function PreferredDeviceSelection({
  cassettePrinterId,
  slidePrinterId,
  cassettePrinterOptions,
  slidePrinterOptions,
  isLoading,
  hasLoadingError,
  hasPartialLoadingError,
  hasStaleCassettePrinter,
  hasStaleSlidePrinter,
  onCassettePrinterChange,
  onSlidePrinterChange,
}: PreferredDeviceSelectionProps) {
  const hasStalePrinter = hasStaleCassettePrinter || hasStaleSlidePrinter;

  return (
    <Stack gap="sm">
      <Stack gap={2}>
        <Text fw={600}>Select Preferred Devices</Text>
        <Text size="sm" c="dimmed">
          Optional overrides for print jobs. Leave a field empty to use the
          printer selected for this workstation.
        </Text>
      </Stack>

      {hasLoadingError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Printers could not be loaded"
          color="red"
          variant="light"
          role="alert"
        >
          Printer preferences cannot be changed right now. Try reopening this
          window.
        </Alert>
      )}

      {hasPartialLoadingError && !hasLoadingError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Some printers could not be checked"
          color="orange"
          variant="light"
          role="status"
        >
          Printers from one or more workstations may be missing from the lists.
          Existing preferences on those workstations will be preserved.
        </Alert>
      )}

      {hasStalePrinter && !hasLoadingError && (
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="A preferred printer is no longer available"
          color="orange"
          variant="light"
          role="alert"
        >
          Clear the unavailable printer or select a replacement before saving.
        </Alert>
      )}

      <SimpleGrid cols={{ base: 1, sm: 2 }}>
        <Select
          label="Cassette Printer"
          description="Optional"
          placeholder="Use workstation printer"
          searchable
          clearable
          disabled={isLoading || hasLoadingError}
          data={cassettePrinterOptions}
          value={cassettePrinterId}
          onChange={onCassettePrinterChange}
          nothingFoundMessage="No cassette printers found"
          comboboxProps={{ withinPortal: true, position: "bottom-start" }}
        />
        <Select
          label="Slide Printer"
          description="Optional"
          placeholder="Use workstation printer"
          searchable
          clearable
          disabled={isLoading || hasLoadingError}
          data={slidePrinterOptions}
          value={slidePrinterId}
          onChange={onSlidePrinterChange}
          nothingFoundMessage="No slide printers found"
          comboboxProps={{ withinPortal: true, position: "bottom-start" }}
        />
      </SimpleGrid>
    </Stack>
  );
}
